import PlayerManager, { TMediaEvent } from './playerManager';
import BaseVideoPlayer, { TEventListener, IVideoPlayerOptions } from './base';
export type { ITimeUpdateParams } from './base';
import { DRAGGABLE_TYPE } from '@umu/umu-types';

class VideoPlayer extends BaseVideoPlayer {
    public player: any;
    private mod: 'native' | 'tc' = 'native';
    private loadSuccessCallback: (() => void) | null = null;
    constructor(options: IVideoPlayerOptions) {
        super(options);
        if (options.fileId) {
            this.mod = 'tc';
        }
    }

    public play() {
        if (this.mod === 'tc') {
            if (this.player.paused()) {
                this.player.play();
            }
        } else {
            super.play();
        }
    }

    public togglePlay() {
        if (this.mod === 'tc') {
            if (this.player.paused()) {
                this.player.play();
            } else {
                this.player.pause();
            }
        } else {
            super.togglePlay();
        }
    }

    public stop() {
        if (this.mod === 'tc') {
            this.player.pause();
        } else {
            super.stop();
        }
    }

    public seekTime(time: number) {
        // 验证时间值是否有效（不是 NaN、Infinity 或负数）
        if (!Number.isFinite(time) || time < 0) {
            return;
        }
        if (this.mod === 'tc') {
            if (this.player.paused()) {
                this.isPlayerSeek = true;
            }
            this.player.currentTime(time);
        } else {
            if (this.videoPlayer?.paused) {
                this.isPlayerSeek = true;
            }
            super.seekTime(time);
        }
    }

    public seekPercent(percent: number) {
        if (this.mod === 'tc') {
            const duration = this.player.duration() || 0;
            // 验证 duration 是否有效
            if (!Number.isFinite(duration) || duration <= 0) {
                return;
            }
            const time = duration * percent;
            this.seekTime(time);
        } else {
            super.seekPercent(percent);
        }
    }

    public setPlaybackRate(rate: number) {
        if (this.mod === 'tc') {
            this.getCurrentPlayer().playbackRate = rate;
        } else {
            super.setPlaybackRate(rate);
        }
    }

    public destroy() {
        if (this.mod === 'tc') {
            this.player.dispose();
        } else {
            super.destroy();
        }
    }

    public getCurrentTime() {
        if (this.mod === 'tc') {
            return this.player.currentTime();
        }

        return super.getCurrentTime();
    }

    public onLoadPlayerSuccess(fn: () => void) {
        this.loadSuccessCallback = fn;
    }

    protected init() {
        if (this.options.fileId) {
            super.initVideoEventMap();
            this.initVideo();
        } else {
            super.init();
        }
    }

    protected initVideo() {
        if (this.options.fileId) {
            this.initTCPlayer();
        } else if (this.options.useNativeVideo) {
            this.initNativeVideoPlayer();
        } else {
            return super.initVideo();
        }
    }

    protected initNativeVideoPlayer() {
        this.playerId = PlayerManager.add('video', this.options);
        this.videoPlayer = PlayerManager.getPlayer('video', this.playerId)!;
        PlayerManager.load(this.playerId)!;
        const video = this.videoPlayer!;
        const options = this.options;
        video.autoplay = !!options.autoplay;
        video.controls = !!options.controls;
        video.volume = (options.volume || 0) / 100;
        // HLS (m3u8) src is set by PlayerManager.load() → attachHls(); direct assignment would abort the MSE session
        if (!PlayerManager.hasHls(this.playerId)) {
            video.src = options.file;
        }
        // 如果是webm格式的视频，不设置poster
        if (options.poster) {
            try {
                const { pathname } = new URL(options.poster);
                if (!pathname.endsWith('.webm')) {
                    video.poster = options.poster;
                }
            } catch (e) {}
        }

        video.setAttribute('preload', 'metadata');
        video.setAttribute('fetchpriority', 'high');
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.setAttribute('x5-video-player-type', 'h5');
        video.setAttribute('x5-video-player-fullscreen', 'true');
        video.setAttribute('x5-video-orientation', 'landscape|portrait');

        const controlsItems = ['nodownload'];
        if (!this.options.allowChangePlayRate) {
            controlsItems.push('noplaybackrate');
        }

        const controlsString = controlsItems.join(' ');
        video.setAttribute('webkit-controlslist', controlsString);
        video.setAttribute('x5-controlslist', controlsString);
        video.setAttribute('controlslist', controlsString);

        if (this.container) {
            this.container.appendChild(video);
        }

        video.style.width = '100%';
        video.style.height = '100%';
        video.style.maxWidth = '100%';
    }

    // TCPlayer点播播放器文档：https://cloud.tencent.com/document/product/266/83131
    private initTCPlayer() {
        const video = document.createElement('video');
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.setAttribute('preload', 'auto');
        video.setAttribute('controlsList', 'nodownload nofullscreen');
        if (this.container) {
            this.container.appendChild(video);
        }
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.maxWidth = '100%';
        const { fileId, psign, appID } = this.options;
        if (fileId) {
            this.player = window.TCPlayer(video, {
                fileID: fileId,
                // app_id上线前需要改回线上值
                appID,
                psign,
                hlsConfig: {},
                controlBar: false,
                controls: !!this.options.useNativeVideo,
            });
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            this.player.ready(() => {});
            this.player.on('loadstart', () => {
                this.videoPlayer = this.getCurrentPlayer();
                this.playerId = PlayerManager.add('video', this.options);
                PlayerManager.bindPlayerWithUniqueId(this.player, this.playerId);
                PlayerManager.load(this.playerId);
                PlayerManager.bindEvent(this.videoPlayer!, this.playerId);
                super.bindEvent();
                this.loadSuccessCallback && this.loadSuccessCallback();
            });
        }
    }

    private getCurrentPlayer() {
        return this.player.el().children[0];
    }
}

// 处理 IOS 控制中心里快进视频的问题
export class VideoUseAntiCheating extends VideoPlayer {
    public lastCurrentTime = 0;
    public stopDrag = false;
    public pageVisibility?: any;
    public maxPlayedTime = 0;
    public useAntiCheating = false;

    constructor(options: IVideoPlayerOptions) {
        super(options);
        this.lastCurrentTime = this.maxPlayedTime = options.lastPlayedTime || 0;
        this.stopDrag = options.draggableType === DRAGGABLE_TYPE.DRAGGABLE_PLAYED;
        this.useAntiCheating = !options.useNativeVideo && this.stopDrag;
        if (this.useAntiCheating) {
            // 当 this.options.fileId 有值时，会使用 hls 来播放视频，这个时候 videoPlayer 是异步加载的，所以没有办法直接调用 this.videoPlayer
            // 将这个 checkPlaybackProgress 放到 onLoadPlayerSuccess 中，确保 videoPlayer 已经加载完成再调用
            if (this.options.fileId) {
                super.onLoadPlayerSuccess(() => {
                    this.checkPlaybackProgress();
                });
            } else {
                this.checkPlaybackProgress();
            }
        }
    }

    private rollbackHandler = () => {
        const currentTime = this.getCurrentTime();
        if (this.useAntiCheating && currentTime - this.maxPlayedTime > 2) {
            this.fastSeek(this.lastCurrentTime);
            this.onIllegalDragTrack?.(this.lastCurrentTime, currentTime);
        } else {
            this.lastCurrentTime = currentTime;
            this.maxPlayedTime = Math.max(this.maxPlayedTime, currentTime);
        }
    };

    private checkPlaybackProgress = () => {
        this.videoPlayer!.addEventListener('timeupdate', this.rollbackHandler, false);
    };

    public updateDraggableType = (draggableType: DRAGGABLE_TYPE) => {
        this.stopDrag = draggableType === DRAGGABLE_TYPE.DRAGGABLE_PLAYED;
        if (!this.stopDrag) {
            this.useAntiCheating = false;
            this.videoPlayer!.removeEventListener('timeupdate', this.rollbackHandler, false);
        }
    };

    private emitEventCheckDrag = (listener: (...args: any[]) => void) => {
        const that = this;
        return (...args: any[]) => {
            if (that.useAntiCheating && this.getCurrentTime() - this.maxPlayedTime > 2) {
                return;
            }
            listener(...args);
        };
    };

    public on(eventName: TEventListener | TMediaEvent, listener: (...args: any[]) => void) {
        let callback = listener;

        if (
            this.useAntiCheating &&
            (eventName === 'ended' || eventName === 'timeupdate' || eventName === 'currentTimeUpdate')
        ) {
            callback = this.emitEventCheckDrag(listener);
        }

        super.on(eventName, callback);
    }

    public destroy() {
        super.destroy();
    }
}

export { VideoPlayer };
